import React from 'react';
import NewProjectWizardCard from './NewProjectWizardCard';
import { draftFromGroup, groupAccessLines } from './accessGroupCatalog';
import { getInitials } from '../../utils/commonUtils';
import { isWorkEmail, workEmailError } from '../../utils/workEmail';
import { TEAM_SETUP_STEPS } from './ndaDraft';
import styles from './css/index.module.css';

function parseEmails(raw) {
    return String(raw || '')
        .split(/[\s,;]+/)
        .map(item => item.trim().toLowerCase())
        .filter(Boolean);
}

export default function SetupAddMembers({
    user,
    groups = [],
    extras = {},
    workstreams = [],
    invitesByGroup = {},
    onInvitesChange,
    adminEmails = [],
    onAdminEmailsChange,
    onReview,
    onBack,
    onSkip,
    onAddGroup,
}) {
    const [showAdminInput, setShowAdminInput] = React.useState(adminEmails.length > 0);
    const displayName = user?.full_name || '';
    const email = user?.email || '';
    const initials = getInitials(displayName) || (email ? email.slice(0, 2).toUpperCase() : '');

    const setGroupEmails = (groupId, emails) => {
        onInvitesChange(prev => ({ ...prev, [groupId]: emails }));
    };

    return (
        <NewProjectWizardCard
            step={2}
            totalSteps={TEAM_SETUP_STEPS}
            accessWide
            title="Add team members"
            subtitle="Invite people to the access groups you created. Members inherit the access configured for their group."
            continueLabel="Continue"
            onContinue={onReview}
            onBack={onBack}
            backLabel="Back"
            backInline
            onSkip={onSkip}
            skipLabel="Skip team setup & enter project"
            skipEnd
        >
            <p className={styles.agGroupLabel}>Project Administrators</p>
            <div className={styles.agAdminCard}>
                <div className={styles.agAdminTop}>
                    <span className={styles.agAvatar} aria-hidden="true">
                        {initials}
                    </span>
                    <div>
                        <p className={styles.agAdminName}>
                            {displayName || email || 'You'}
                            <span className={styles.wsRecommendedBadge}>You</span>
                        </p>
                        {email ? <p className={styles.agAdminEmail}>{email}</p> : null}
                    </div>
                </div>
                {showAdminInput ? (
                    <div className={styles.tmAdminInvite}>
                        <EmailField emails={adminEmails} onChange={onAdminEmailsChange} placeholder="Enter email addresses" />
                    </div>
                ) : (
                    <button type="button" className={`${styles.agTextLink} ${styles.tmAdminAdd}`} onClick={() => setShowAdminInput(true)}>
                        + Add another administrator
                    </button>
                )}
            </div>

            {groups.map(group => {
                const extra = extras[group.id] || draftFromGroup(group, workstreams);
                const lines = groupAccessLines(extra, workstreams);
                const emails = invitesByGroup[group.id] || [];
                return (
                    <div key={group.id} className={styles.tmGroupCard}>
                        <div className={styles.tmGroupHead}>
                            <h3 className={styles.tmGroupTitle}>{group.name}</h3>
                            <p className={styles.tmGroupMeta}>{lines.headline}</p>
                            <p className={styles.tmGroupMeta}>{lines.vdr}</p>
                            <p className={styles.tmGroupMeta}>{lines.intelligence}</p>
                            <p className={styles.tmGroupMeta}>{lines.projectMail}</p>
                            <p className={styles.tmGroupMeta}>{lines.projectMeetings}</p>
                        </div>
                        <div className={styles.tmGroupBody}>
                            <p className={styles.tmAddLabel}>Add members</p>
                            <EmailField emails={emails} onChange={next => setGroupEmails(group.id, next)} />
                            {emails.map(item => (
                                <div key={item} className={styles.tmPending}>
                                    <div>
                                        <p className={styles.tmPendingEmail}>{item}</p>
                                        <p className={styles.tmPendingHint}>Will be invited</p>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.tmPendingRemove}
                                        onClick={() => setGroupEmails(group.id, emails.filter(emailItem => emailItem !== item))}
                                        aria-label={`Remove ${item}`}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}

            <button type="button" className={`${styles.addScopeLink} ${styles.agAddLink}`} onClick={onAddGroup}>
                + Add another access group
            </button>
        </NewProjectWizardCard>
    );
}

const EmailField = ({ emails = [], onChange, placeholder = 'Enter email addresses' }) => {
    const [value, setValue] = React.useState('');
    const [error, setError] = React.useState('');
    const inputRef = React.useRef(null);

    const commitEmails = raw => {
        const incoming = parseEmails(raw);
        if (!incoming.length) return;
        const next = [...emails];
        let nextError = '';
        incoming.forEach(item => {
            const message = workEmailError(item);
            if (message) {
                nextError = message;
                return;
            }
            if (!next.includes(item)) next.push(item);
        });
        setError(nextError);
        if (next.length !== emails.length) onChange(next);
        if (!nextError) setValue('');
        else if (incoming.length === 1) setValue(incoming[0]);
        else setValue('');
    };

    const onInputChange = event => {
        const next = event.target.value;
        if (/[,;\s]$/.test(next)) {
            commitEmails(next);
            return;
        }
        setValue(next);
        if (error) setError('');
    };

    const onKeyDown = event => {
        if (event.key === 'Enter' || event.key === 'Tab') {
            if (!value.trim()) return;
            event.preventDefault();
            commitEmails(value);
        } else if (event.key === 'Backspace' && !value && emails.length) {
            onChange(emails.slice(0, -1));
        }
    };

    const onPaste = event => {
        const text = event.clipboardData?.getData('text');
        if (!text || !/[,;\s]/.test(text)) return;
        event.preventDefault();
        commitEmails(`${value} ${text}`);
    };

    const onBlur = () => {
        if (isWorkEmail(value.trim())) commitEmails(value);
        else if (value.trim()) setError(workEmailError(value));
    };

    return (
        <div>
        <div className={`${styles.tmChipBox} ${error ? styles.tmChipBoxError : ''}`} onClick={() => inputRef.current?.focus()}>
            {emails.map(item => (
                <span key={item} className={styles.tmChip}>
                    {item}
                    <button
                        type="button"
                        className={styles.tmChipRemove}
                        onClick={event => {
                            event.stopPropagation();
                            onChange(emails.filter(emailItem => emailItem !== item));
                        }}
                        aria-label={`Remove ${item}`}
                    >
                        ×
                    </button>
                </span>
            ))}
            <input
                ref={inputRef}
                className={styles.tmChipInput}
                value={value}
                onChange={onInputChange}
                onKeyDown={onKeyDown}
                onPaste={onPaste}
                onBlur={onBlur}
                placeholder={emails.length ? '' : placeholder}
                aria-label={placeholder}
                autoComplete="off"
            />
        </div>
        {error ? <p className={styles.tmEmailError}>{error}</p> : null}
        </div>
    );
};
